import {toRadians} from "./Utils";
import * as THREE from "three";

export class CBAROrientationControls {
    private object: any;
    private enabled: boolean;
    private deviceOrientation: any;
    private screenOrientation: number;
    private alpha: number;
    private alphaOffsetAngle: number;
    private betaOffsetAngle: number;
    private gammaOffsetAngle: number;

    constructor(object: any) {
        this.object = object;
        this.object.rotation.reorder("YXZ");
        this.enabled = true;
        this.deviceOrientation = {};
        this.screenOrientation = 0;
        this.alpha = 0;
        this.alphaOffsetAngle = 0;
        this.betaOffsetAngle = 0;
        this.gammaOffsetAngle = 0;

        this.connect();
    }

    private onDeviceOrientationChangeEvent = (event: any) => {
        this.deviceOrientation = event;
    };

    private onScreenOrientationChangeEvent = () => {
        this.screenOrientation = window.orientation || 0;
    };

    private setObjectQuaternion = (() => {
        const zee = new THREE.Vector3(0, 0, 1);
        const euler = new THREE.Euler();
        const q0 = new THREE.Quaternion();
        const q1 = new THREE.Quaternion(
            -Math.sqrt(0.5),
            0,
            0,
            Math.sqrt(0.5)
        ); // - PI/2 around the x-axis
        return (quaternion: any, alpha: number, beta: number, gamma: number, orient: number) => {
            euler.set(beta, alpha, -gamma, "YXZ"); // 'ZXY' for the device, but 'YXZ' for us
            quaternion.setFromEuler(euler); // orient the device
            quaternion.multiply(q1); // camera looks out the back of the device, not the top
            quaternion.multiply(q0.setFromAxisAngle(zee, -orient)); // adjust for screen orientation
        };
    })();

    connect() {
        this.onScreenOrientationChangeEvent(); // run once on load
        window.addEventListener("orientationchange", this.onScreenOrientationChangeEvent, false);
        window.addEventListener("deviceorientation", this.onDeviceOrientationChangeEvent, false);
        this.enabled = true;
    }

    disconnect() {
        window.removeEventListener("orientationchange", this.onScreenOrientationChangeEvent, false);
        window.removeEventListener("deviceorientation", this.onDeviceOrientationChangeEvent, false);
        this.enabled = false;
    }

    update() {
        if (this.enabled === false) return;
        const alpha = this.deviceOrientation.alpha
            ? toRadians(this.deviceOrientation.alpha) + this.alphaOffsetAngle
            : 0; // Z
        const beta = this.deviceOrientation.beta
            ? toRadians(this.deviceOrientation.beta) + this.betaOffsetAngle
            : 0; // X'
        const gamma = this.deviceOrientation.gamma
            ? toRadians(this.deviceOrientation.gamma) + this.gammaOffsetAngle
            : 0; // Y''
        const orient = this.screenOrientation ? toRadians(this.screenOrientation) : 0; // O
        this.setObjectQuaternion(this.object.quaternion, alpha, beta, gamma, orient);
        this.alpha = alpha;
    }

    updateAlphaOffsetAngle(angle: number) {
        this.alphaOffsetAngle = angle;
        this.update();
    }

    updateBetaOffsetAngle(angle: number) {
        this.betaOffsetAngle = angle;
        this.update();
    }

    updateGammaOffsetAngle(angle: number) {
        this.gammaOffsetAngle = angle;
        this.update();
    }

    dispose() {
        this.disconnect();
    }
}